// Client-side video → still frames. The Vision AI model wants an
// image; a video's just three well-picked frames. We pick at 25%,
// 50%, 75% of the duration so a lucky first-frame catch and a stable
// mid-clip frame both contribute. Frames come back as JPEG data URIs
// so the identify-video endpoint can decode them with the same seam
// as identify-photo.

const FRAME_QUALITY = 0.8;
const MAX_DIMENSION = 1024; // scale down large videos to keep payload sane
// Per-frame timeout. A malformed video can hang the seek indefinitely
// (no "seeked" event, no "error"), leaving the modal spinning forever.
const SEEK_TIMEOUT_MS = 15000;

export interface ExtractFramesOptions {
  /** Aborts the seek loop between frames. */
  signal?: AbortSignal;
  /** Fires after each frame is captured so the UI can show "Frame N of M". */
  onProgress?: (done: number, total: number) => void;
}

function seekTo(
  video: HTMLVideoElement,
  t: number,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    let done = false;
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      cleanup();
      reject(new Error("Video seek timed out — the file may be malformed."));
    }, SEEK_TIMEOUT_MS);
    const cleanup = () => {
      clearTimeout(timer);
      video.removeEventListener("seeked", onSeek);
      video.removeEventListener("error", onErr);
      signal?.removeEventListener("abort", onAbort);
    };
    const onSeek = () => {
      if (done) return;
      done = true;
      cleanup();
      resolve();
    };
    const onErr = () => {
      if (done) return;
      done = true;
      cleanup();
      reject(new Error("Couldn't seek to that timestamp."));
    };
    const onAbort = () => {
      if (done) return;
      done = true;
      cleanup();
      reject(new Error("Video frame extraction was cancelled."));
    };
    video.addEventListener("seeked", onSeek, { once: true });
    video.addEventListener("error", onErr, { once: true });
    signal?.addEventListener("abort", onAbort, { once: true });
    try {
      video.currentTime = t;
    } catch (e) {
      if (done) return;
      done = true;
      cleanup();
      reject(e);
    }
  });
}

/**
 * Extract N still frames from a video file at even intervals.
 * Returns JPEG data URIs suitable for POST to /api/identify-video.
 *
 * On mobile Safari a background/canvas hoop is sometimes needed to
 * avoid black frames — we set playsInline and mute, then let the
 * browser render each seek before drawing to the canvas.
 */
export async function extractFrames(
  file: File | Blob,
  count = 3,
  opts: ExtractFramesOptions = {},
): Promise<string[]> {
  if (count < 1 || count > 5) throw new Error("count must be 1..5");
  const { signal, onProgress } = opts;
  if (signal?.aborted) throw new Error("Video frame extraction was cancelled.");
  const video = document.createElement("video");
  video.playsInline = true;
  video.muted = true;
  video.preload = "auto";
  const url = URL.createObjectURL(file);
  try {
    video.src = url;
    await new Promise<void>((resolve, reject) => {
      const onAbort = () =>
        reject(new Error("Video frame extraction was cancelled."));
      signal?.addEventListener("abort", onAbort, { once: true });
      video.addEventListener("loadedmetadata", () => resolve(), { once: true });
      video.addEventListener("error", () =>
        reject(new Error("Couldn't read that video file.")),
      );
    });
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    if (duration <= 0) throw new Error("Video has no readable duration.");

    // Compute scaled dimensions once.
    const w = video.videoWidth || 640;
    const h = video.videoHeight || 480;
    const scale = Math.min(1, MAX_DIMENSION / Math.max(w, h));
    const targetW = Math.max(1, Math.round(w * scale));
    const targetH = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D unavailable.");

    const frames: string[] = [];
    for (let i = 0; i < count; i++) {
      if (signal?.aborted) {
        throw new Error("Video frame extraction was cancelled.");
      }
      const t = duration * ((i + 1) / (count + 1));
      await seekTo(video, t, signal);
      ctx.drawImage(video, 0, 0, targetW, targetH);
      frames.push(canvas.toDataURL("image/jpeg", FRAME_QUALITY));
      onProgress?.(i + 1, count);
    }
    return frames;
  } finally {
    URL.revokeObjectURL(url);
  }
}
